import { TeamRole } from "@repo/api/team-roles";
import {
  teamQueryKeys,
  useDeleteTeamMemberMutation,
  useUpdateTeamMemberMutation,
} from "@/lib/queries/team-queries";
import { TeamMembersDto } from "@repo/api/team";
import Toast from "@/lib/components/Toast";
import { queryClient } from "@/lib/queries/query-client";
import { useSelfQuery } from "@/lib/queries/user-queries";
import { MemberItem } from "./MemberItem";
import { getFullname } from "@/lib/utils";

type SortedMember = TeamMembersDto["members"][number] & { fullname: string };

function sortMembers(
  members: TeamMembersDto["members"],
  userExternalId: string | undefined,
): SortedMember[] {
  return members
    .map((member) => ({
      ...member,
      fullname: getFullname(member.firstName, member.lastName),
    }))
    .sort((a, b) => {
      if (a.userExternalId === userExternalId) return -1;
      if (b.userExternalId === userExternalId) return 1;

      return a.fullname.localeCompare(b.fullname);
    });
}

export default function ActiveMembers({
  isMobile = false,
  teamExternalId,
  members,
  userIsAllowedToEdit,
}: {
  isMobile?: boolean;
  teamExternalId: string;
  members: TeamMembersDto["members"];
  userIsAllowedToEdit: boolean;
}) {
  const { data: user } = useSelfQuery();

  const { mutate: updateMemberRole } = useUpdateTeamMemberMutation({
    teamExternalId,
    onMutate: async ({ teamMemberExternalId, data }) => {
      await queryClient.cancelQueries({
        queryKey: teamQueryKeys.getTeamMembers(teamExternalId),
      });

      const previousData = queryClient.getQueryData<TeamMembersDto>(
        teamQueryKeys.getTeamMembers(teamExternalId),
      );

      if (previousData) {
        const updatedMembers = previousData.members.map((member) =>
          member.externalId === teamMemberExternalId
            ? { ...member, ...data }
            : member,
        );

        queryClient.setQueryData(teamQueryKeys.getTeamMembers(teamExternalId), {
          ...previousData,
          members: updatedMembers,
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          teamQueryKeys.getTeamMembers(teamExternalId),
          context.previousData,
        );
      }

      Toast.error("Failed to update member role");
    },
  });

  const { mutate: deleteMember } = useDeleteTeamMemberMutation({
    teamExternalId,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: teamQueryKeys.getTeamMembers(teamExternalId),
        type: "all",
      });
    },
    onError: () => {
      Toast.error("Failed to delete member");
    },
  });

  const onRoleChange = (teamMemberExternalId: string, role: TeamRole) => {
    updateMemberRole({
      teamMemberExternalId,
      data: { role },
    });
  };

  const onDelete = (teamMemberExternalId: string) => {
    deleteMember(teamMemberExternalId);
  };

  return (
    <>
      {sortMembers(members, user?.externalId).map((member) => (
        <MemberItem
          isMobile={isMobile}
          key={member.externalId}
          status="Active"
          item={{
            fullname: member.fullname,
            id: member.externalId,
            role: member.role,
            email: member.contactEmail,
          }}
          isSelf={user?.externalId === member.userExternalId}
          userIsAllowedToEdit={userIsAllowedToEdit}
          onRoleChange={onRoleChange}
          onDelete={onDelete}
        />
      ))}
    </>
  );
}
